import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useConfirm } from '../ConfirmDialog';

/**
 * A PERSON'S PERSONAL MARKS (131; Gordon, 4 November), on the register
 * filtered to them (?holder=u:<id> or c:<id>) - the page that lists what
 * somebody holds. Administrators only, as the server is.
 *
 * A mark is a thing they said is their own - by its serial, or by its make
 * and model - which Discovery never links to the firm: a laptop of theirs
 * reporting it sends it straight to Ignored. One that is wrong (the monitor
 * was the firm's after all) is removed here, after a confirm; the Audit log
 * keeps the line. Nothing is shown when they have none.
 *
 *   holder   'u:<id>' / 'c:<id>'
 *   meta     the register's meta, for their name as the Who has it list says it
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

/** "Dell U2722D", else what kind of thing it is. */
const thingOf = (m) => [m.make, m.model].filter(Boolean).join(' ') || m.kind_label || 'Something';
/** What it is matched on, in words. */
const byOf = (m) => [m.by_serial && m.serial && `serial ${m.serial}`, m.by_model && 'any of this make and model']
  .filter(Boolean).join(', ');

/** A holder key's name, from the register's meta. */
function nameOf(meta, key) {
  const id = String(key || '').slice(2);
  if (String(key).startsWith('c:')) return (meta?.contractors || []).find((c) => c.id === id)?.name || '';
  const u = (meta?.users || []).find((x) => x.id === id);
  return u ? (u.display_name || u.email) : '';
}

export default function PersonalMarks({ holder, meta }) {
  const name = nameOf(meta, holder);
  const [confirm, confirmDialog] = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // A quick change of person must not let the previous person's answer win.
  const seq = useRef(0);

  const load = useCallback(async () => {
    seq.current += 1;
    const mine = seq.current;
    try {
      const r = await axios.get(`${API_BASE}/asset-admin/personal-marks`, { params: { holder } });
      if (mine === seq.current) { setData(r.data); setError(''); }
    } catch (e) {
      if (mine === seq.current) { setData(null); setError(e?.response?.status === 503 ? '' : errorOf(e, 'Could not load their personal marks.')); }
    }
  }, [holder]);
  useEffect(() => { setData(null); load(); }, [load]);

  const remove = async (m) => {
    const ok = await confirm({
      title: `Remove the personal mark on ${thingOf(m)}?`, confirmLabel: 'Remove', danger: true,
      message: `Discovery will treat it as anybody’s again: the next time ${name ? `one of ${name}’s laptops` : 'one of their laptops'} reports it, it waits in the inbox instead of going to Ignored.`
    });
    if (!ok) return;
    setBusy(true);
    setError('');
    try {
      await axios.delete(`${API_BASE}/asset-admin/personal-marks/${m.id}`);
      await load();
    } catch (e) {
      setError(errorOf(e, 'Could not remove the mark.'));
    } finally {
      setBusy(false);
    }
  };

  if (!data && !error) return null;
  const marks = data?.marks || [];
  if (!marks.length && !error) return null;
  return (
    <section className="as-panel as-personal-marks" aria-label="Personal marks" data-testid="personal-marks">
      <h2>{`Personal marks${name ? ` - ${name}` : ''}`}</h2>
      <p className="as-quiet">
        Things they said are their own. Discovery never links these to the firm: a report of one goes straight to Ignored.
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}
      <ul className="as-tree">
        {marks.map((m) => (
          <li key={m.id} data-mark={m.id}>
            <span>
              <strong>{`Personal: ${thingOf(m)}`}</strong>
              {byOf(m) && <span className="as-quiet">{` (${byOf(m)})`}</span>}
            </span>
            <span className="as-spacer" />
            {data?.removable && (
              <button type="button" className="btn btn-plain btn-small as-mark-remove" disabled={busy}
                aria-label={`Remove the personal mark on ${thingOf(m)}`} onClick={() => remove(m)}>Remove</button>
            )}
          </li>
        ))}
      </ul>
      {data && !data.removable && <p className="as-quiet">Removing a mark needs migration 131.</p>}
      {confirmDialog}
    </section>
  );
}
